import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import classNames from 'classnames';
import { useViewport } from '@sbt-web/hooks';
import type { AdItem, SearchResult } from '@sbt-web/network/types';
import { PaginationComponent } from '@sbt-web/ui';
import { AdItemCardMiniSkeleton } from '@sbt-web/item-card';
import { getUserAds } from '@/api/getUserAds';
import FavoritesContainer from '@/client/components/Search/Containers/FavoritesContainer';
import useUser from '@/client/contexts/UserContext';
import { PROFILE_ADS_PER_PAGE } from '@/shared/constants';
import { isProfileOwner } from '@/utils/profile';
import { Card } from './components/Card';
import styles from './index.module.scss';
interface Props {
  userAdsData?: SearchResult;
  ownerId: string;
  showProAds?: boolean;
  isPro?: boolean;
}
interface AdItemListProps {
  adItems: AdItem[];
  isProfileOwner: boolean;
  loading?: boolean;
}
const MIN_ITEMS_TO_SHOW = 3;
const SKELETON_ITEMS = Array.from({
  length: MIN_ITEMS_TO_SHOW
}).map((_, index) => ({
  id: index
}));
const AdItemList = t0 => {
  const $ = _c(8);
  const {
    adItems,
    isProfileOwner,
    loading
  } = t0;
  if (loading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ul className={styles.ad_grid}>{SKELETON_ITEMS.map(_temp)}</ul>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== adItems || $[2] !== isProfileOwner) {
    let t2;
    if ($[4] !== isProfileOwner) {
      t2 = (adItem, index) => <li key={adItem.urn} className={styles.ad_grid_item}><Card adItem={adItem} index={index} isProfileOwner={isProfileOwner} /></li>;
      $[4] = isProfileOwner;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = adItems.map(t2);
    $[1] = adItems;
    $[2] = isProfileOwner;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <ul className={styles.ad_grid}>{t1}</ul>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export default function Listing(t0) {
  const $ = _c(26);
  const {
    userAdsData,
    ownerId,
    showProAds: t1,
    isPro: t2
  } = t0;
  const showProAds = t1 === undefined ? false : t1;
  const isPro = t2 === undefined ? false : t2;
  const [userAdsDataState, setUserAdsDataState] = useState(userAdsData);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  let t3;
  if ($[0] !== userAdsDataState?.ads) {
    t3 = userAdsDataState?.ads ?? [];
    $[0] = userAdsDataState?.ads;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const adItems = t3;
  const adItemNumber = userAdsData?.total ?? 0;
  const itemPerPage = userAdsData?.lines ?? PROFILE_ADS_PER_PAGE;
  let lastPage = 0;
  if (itemPerPage > 0) {
    lastPage = Math.ceil(adItemNumber / itemPerPage);
  }
  const {
    isDesktop,
    isMobile
  } = useViewport();
  const {
    user
  } = useUser();
  const t4 = user?.id;
  let t5;
  if ($[2] !== ownerId || $[3] !== t4) {
    t5 = isProfileOwner(ownerId, t4);
    $[2] = ownerId;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const isOwner = t5;
  let t6;
  if ($[5] !== isPro || $[6] !== itemPerPage || $[7] !== ownerId || $[8] !== showProAds) {
    t6 = page => {
      setLoading(true);
      setCurrentPage(page);
      const start = (page - 1) * itemPerPage;
      getUserAds(ownerId, isPro, showProAds, start, itemPerPage).then(data => {
        setUserAdsDataState(data);
        setLoading(false);
      });
    };
    $[5] = isPro;
    $[6] = itemPerPage;
    $[7] = ownerId;
    $[8] = showProAds;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handlePageChange = t6;
  if (!isDesktop && isOwner) {
    return null;
  }
  let t7;
  if ($[10] !== adItemNumber || $[11] !== isMobile) {
    t7 = (isMobile || adItemNumber > 0) && <h5 className={classNames(styles.title, "headline-5")}>Annunci online</h5>;
    $[10] = adItemNumber;
    $[11] = isMobile;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== adItemNumber || $[14] !== adItems || $[15] !== isOwner || $[16] !== loading) {
    t8 = adItemNumber > 0 ? <FavoritesContainer skipTrackings={true}><AdItemList adItems={adItems} isProfileOwner={isOwner} loading={loading} /></FavoritesContainer> : <div className={styles.no_ads_text}>Nessun annuncio attivo</div>;
    $[13] = adItemNumber;
    $[14] = adItems;
    $[15] = isOwner;
    $[16] = loading;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== currentPage || $[19] !== handlePageChange || $[20] !== lastPage) {
    t9 = <PaginationComponent lastPage={lastPage} currentPage={currentPage} changePage={handlePageChange} />;
    $[18] = currentPage;
    $[19] = handlePageChange;
    $[20] = lastPage;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = <section className={styles.listing}>{t7}{t8}{t9}</section>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}
function _temp(t0) {
  const {
    id
  } = t0;
  return <li key={id} className={styles.ad_grid_item}><AdItemCardMiniSkeleton imageSizeType="responsive" aspectRatio="4/5" /></li>;
}